/**
 * Feature: Marketing Automation
 * /features/marketing-automation
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  Route,
  Mail,
  MessageSquare,
  CloudRain,
  RefreshCw,
  BarChart3,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'Do I need technical skills to build automations?',
    answer:
      "No. Rooflyzer's journey builder is fully visual — drag, drop, and connect steps. No code, no APIs, no technical knowledge required.",
  },
  {
    question: 'What triggers can I use?',
    answer:
      'Lead stage changes, storm events in your territory, date-based delays, email opens/clicks, form submissions, and manual enrollment. More triggers added regularly.',
  },
  {
    question: 'Is marketing automation included in my base plan?',
    answer:
      'Basic automation is included in Pro. The full ConnectIQ Marketing Suite (25,000 emails + 1,000 SMS/month + journey builder + AI nurture) is a $59/month add-on.',
  },
  {
    question: 'Can I send from my own domain?',
    answer:
      'Yes. Connect your sending domain to Rooflyzer for branded email delivery. Improves deliverability and brand recognition.',
  },
  {
    question: 'How does storm trigger automation work?',
    answer:
      'You set a territory (state, zip codes, or radius). When a qualifying storm event is detected (based on hail size or wind speed thresholds you configure), Rooflyzer automatically enrolls affected contacts in your storm outreach journey.',
  },
];

const marketingAutomationStats = [
  { value: '25,000', label: 'Emails per month', desc: 'In Marketing Suite' },
  { value: '1,000', label: 'SMS messages/month', desc: 'In Marketing Suite' },
  { value: '5x', label: 'Higher SMS open rate', desc: 'vs email' },
  { value: '30%', label: 'Avg re-engagement lift', desc: 'With targeted nurture' },
];

export const MarketingAutomationPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Marketing Automation',
    description:
      'Automate your roofing marketing with email campaigns, SMS sequences, and visual journey builder. Nurture leads 24/7 without lifting a finger.',
    featureList: [
      'Visual Journey Builder',
      'Email Campaign Manager',
      'SMS Sequences',
      'Storm Trigger Automations',
      'Re-Nurture Campaigns',
      'Campaign Analytics',
    ],
    url: 'https://rooflyzer.pro/features/marketing-automation',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Marketing Automation — Email, SMS & Journey Builder"
        description="Automate your roofing marketing with email campaigns, SMS sequences, and visual journey builder. Nurture leads 24/7 without lifting a finger."
        canonicalUrl="/features/marketing-automation"
        keywords="roofing marketing automation, roofing email marketing, roofing SMS marketing, roofing lead nurturing automation"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Marketing Automation', url: '/features/marketing-automation' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Zap}>24/7 Lead Nurturing</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Your Marketing Team —{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Without the Payroll
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Build once, run forever. Rooflyzer's journey builder sends the right message to the
              right lead at exactly the right time — whether that's 3 hours or 3 months after first
              contact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-[#050507] hover:bg-white/90 font-semibold px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Automating Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See ConnectIQ Plans
                </Button>
              </Link>
            </div>
          </section>

          {/* Product Screenshot */}
          <section className="mb-24">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src="/product-frames/marketing.webp"
                alt="Rooflyzer marketing automation hub"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm text-white/90 border border-white/20">
                  Live product screenshot
                </span>
              </div>
            </div>
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Automation Built for Roofing
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: Route,
                  title: 'Visual Journey Builder',
                  desc: 'Drag-and-drop automation builder. Map out every step: trigger → wait → email or SMS → branch on behavior.',
                },
                {
                  icon: Mail,
                  title: 'Email Campaign Manager',
                  desc: 'Design, send, and track email campaigns to any segment of your contact list. 25,000 emails/month included in Marketing Suite.',
                },
                {
                  icon: MessageSquare,
                  title: 'SMS Sequences',
                  desc: 'Build SMS follow-up sequences that feel personal. Open rates 5x higher than email. Included in Marketing Suite.',
                },
                {
                  icon: CloudRain,
                  title: 'Storm Trigger Automations',
                  desc: 'When a storm hits your territory, Rooflyzer automatically launches a storm outreach sequence to all contacts in the affected zip codes.',
                },
                {
                  icon: RefreshCw,
                  title: 'Re-Nurture Campaigns',
                  desc: 'Leads tagged as dormant automatically enter re-engagement sequences based on their specific reason code.',
                },
                {
                  icon: BarChart3,
                  title: 'Campaign Analytics',
                  desc: "Track open rates, click rates, reply rates, and revenue attributed to every campaign. Know what's working.",
                },
              ]}
            />
          </section>

          {/* The Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Manually send follow-up emails one at a time',
                    'Hire someone just to manage marketing',
                    "Miss re-nurture windows because there's no system",
                    'No attribution data on which campaigns generate jobs',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Rooflyzer Automation</h2>
                <FeatureChecklist
                  items={[
                    'Visual automation builder — build once, run forever',
                    'Storm-triggered campaigns launch the moment hail is detected',
                    'Re-nurture sequences based on specific reason codes',
                    'SMS + email in one platform with full campaign attribution',
                    'Revenue tracking per campaign so you know your ROI',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="Marketing Suite by the Numbers" items={marketingAutomationStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Let Automation Do the Heavy Lifting"
            buttonLabel="Start Automating Free"
          >
            Start free. Build your first automation in under 10 minutes. No technical skills
            required.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default MarketingAutomationPage;
